import React from "react";
import { Facebook, Instagram, Linkedin, Youtube, ShieldCheck } from "lucide-react";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const SOCIALS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61589779771836&mibextid=wwXIfr&mibextid=wwXIfr",
    Icon: Facebook,
    bg: "#1877F2",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/ecomcatalogai?igsh=N3hyd204dmhjOXEy",
    Icon: Instagram,
    bg: "linear-gradient(45deg, #833AB4 0%, #E1306C 50%, #FD8D32 100%)",
  },
  {
    label: "YouTube",
    href: "https://youtube.com/@ecomcatalogai?si=RdCRgvyEEfKweZp7",
    Icon: Youtube,
    bg: "#FF0000",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/ecomcatalogai/",
    Icon: Linkedin,
    bg: "#0A66C2",
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/919464645491",
    Icon: WhatsAppIcon,
    bg: "#25D366",
  },
];

/**
 * Floating social bar pinned to the right edge of every marketing page.
 * Full-colour brand buttons that slide out to reveal the label on hover.
 */
export default function SocialBar() {
  return (
    <div
      className="z-40 flex flex-col items-end gap-1.5"
      // Inline styles: `.mkt-shell > *` forces position:relative on direct
      // children, which beats the Tailwind `fixed` class — inline wins.
      style={{
        position: "fixed",
        right: 0,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 40,
      }}
      data-testid="social-bar"
    >
      {SOCIALS.map(({ label, href, Icon, bg }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={`social-bar-${label.toLowerCase()}`}
          className="group flex items-center justify-start gap-2 h-10 w-11 hover:w-36 pl-3 rounded-l-xl text-white overflow-hidden transition-all duration-300 shadow-lg"
          style={{ background: bg }}
        >
          <span className="shrink-0 grid place-items-center">
            <Icon size={17} />
          </span>
          <span className="text-xs font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {label}
          </span>
        </a>
      ))}

      {/* Premium — Official OMS Partner of Myntra badge (sits below WhatsApp) */}
      <OmsPartnerPremiumBadge />
    </div>
  );
}

/**
 * Premium Myntra OMS Partner badge — sits directly below the WhatsApp icon
 * on the SocialBar. Features:
 *   • Animated gradient border (conic-gradient sheen that rotates on hover)
 *   • Myntra pink glow with pulse
 *   • Slides out on hover to reveal the full "Official OMS Partner of Myntra" label
 *   • Verified checkmark badge in the top-right corner
 *   • Matches the exact height & radius language of the social icons above,
 *     so it feels like a natural extension of the bar (not a separate widget)
 */
function OmsPartnerPremiumBadge() {
  return (
    <div
      className="omsp group relative mt-2"
      data-testid="social-bar-oms-partner"
      title="Official OMS Partner of Myntra"
    >
      {/* Animated conic gradient ring */}
      <span className="omsp-ring" aria-hidden="true" />

      {/* Verified check dot */}
      <span
        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0B0D14] grid place-items-center z-10"
        aria-hidden="true"
      >
        <span className="w-1 h-1 rounded-full bg-white" />
      </span>

      <div className="relative flex items-center justify-start gap-2 h-10 w-11 group-hover:w-52 pl-2.5 pr-2 rounded-l-xl overflow-hidden transition-all duration-500 ease-out shadow-[0_10px_30px_-8px_rgba(255,63,108,0.75)]"
        style={{
          background:
            "linear-gradient(135deg, #FF3F6C 0%, #E23768 45%, #FF5A5F 100%)",
        }}
      >
        {/* Shield icon in white circle — reads as "certified" */}
        <span className="shrink-0 w-6 h-6 rounded-full bg-white grid place-items-center shadow-[0_0_10px_rgba(255,255,255,0.4)]">
          <ShieldCheck size={13} className="text-[#FF3F6C]" strokeWidth={2.6} />
        </span>

        {/* Text — hidden until hover */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap leading-tight">
          <div className="text-[7.5px] uppercase tracking-[0.28em] text-white/85 font-black">
            Official
          </div>
          <div className="text-[10.5px] font-black text-white">
            OMS Partner <span className="text-yellow-200">Myntra</span>
          </div>
        </div>

        {/* Diagonal shine sweep on hover */}
        <span className="omsp-shine" aria-hidden="true" />
      </div>
    </div>
  );
}
